import type { WormState } from '../entities/worm';
import type { TankAnimState } from './tankSprites';

/**
 * Animation state machine (resolver form). A single pure function maps the
 * entity's gameplay state + overlay flags to the animation state to play, each
 * frame, in priority order. This is the one place animation priority lives -
 * adding a state is a one-line edit here, not another nested ternary in the
 * render loop.
 *
 * The gameplay FSM is `worm.state` (idle/walking/jumping/...), maintained by
 * physics/input; this maps it (plus overlays like kamikaze/parachute/charge)
 * to the sprite's animation.
 */
export interface AnimResolveContext {
  wormState: WormState;
  kamikazing: boolean;
  parachuteOpen: boolean;
  /** The active/controlled worm is charging a shot (any input method). */
  charging: boolean;
  /** The sprite actually has a dedicated 'charge' sheet (else fall through). */
  hasChargeAnim: boolean;
  /** The worm is moving along the ground (player holding move, or has
   *  horizontal velocity). Keeps the walk anim stable through the single-frame
   *  state blips that physics produces on bumpy terrain - without this, walk
   *  briefly flips to idle and restarts at frame 0 (reads as "not looping"). */
  movingHorizontally: boolean;
}

/** Base gameplay-state -> animation mapping (no overlays). */
export function animForWormState(state: WormState): TankAnimState {
  switch (state) {
    case 'walking':
      return 'walk';
    case 'jumping':
      return 'jump';
    case 'backflipping':
      return 'backflip';
    case 'falling':
      return 'fall';
    case 'dead':
      return 'die';
    default:
      // idle / aiming / firing / drowning -> idle
      return 'idle';
  }
}

/** Resolve the animation to play this frame, highest priority first. */
export function resolveTankAnimState(ctx: AnimResolveContext): TankAnimState {
  if (ctx.kamikazing) return 'kamikaze';
  if (ctx.parachuteOpen) return 'parachute';
  if (ctx.charging && ctx.hasChargeAnim) return 'charge';
  const base = animForWormState(ctx.wormState);
  // Grounded movement: a transient 'idle' frame mid-walk shouldn't drop the
  // walk loop. (Airborne states - jump/fall/backflip - still win, so a real
  // hop reads correctly.)
  if (base === 'idle' && ctx.movingHorizontally) return 'walk';
  return base;
}
